import { useState } from "react";
import { DJSEMApiClient, PanelShell, Button, TextField, tokens } from "@djsem/ui";

// Anonymous live audience feedback page (prompt 150, spec `04_LIVE_SET_FEEDBACK_SPEC.md`). Reached
// via the QR code a DJ generates for a live session — /feedback/:token — with no DJSEM account,
// no login, and no personal data required (the unguessable token in the URL is the only gate;
// see apps/analysis-worker/src/routes/feedbackHandlers.ts's `liveFeedbackAudienceSubmit`).
//
// Same "plain page, no router" pattern as LicensePages.tsx — checked in main.tsx via
// window.location.pathname rather than added to a router this app doesn't have.

const EMOJIS = ["🔥", "🙌", "😍", "😐", "😴"];

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: tokens.color.bg, padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 18, fontFamily: "\"Cormorant Garamond\", Georgia, serif", fontSize: 22, letterSpacing: "0.1em", color: tokens.color.textDim }}>
          DJSEM
        </div>
        {children}
      </div>
    </div>
  );
}

/** Anonymous audience QR feedback form — emoji + optional rating/text/track request, no account. */
export function LiveAudienceFeedbackPage({ client, token }: { client: DJSEMApiClient; token: string }) {
  const [emoji, setEmoji] = useState<string | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [trackRequest, setTrackRequest] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");

  const submit = async () => {
    setStatus("submitting");
    const res = await client.submitLiveAudienceFeedback(token, {
      emoji: emoji ?? undefined, rating: rating ?? undefined, text: text.trim() || undefined, trackRequest: trackRequest.trim() || undefined,
    });
    setStatus(res.ok ? "done" : "error");
  };

  if (status === "done") {
    return (
      <PageShell>
        <PanelShell title="Thanks!">
          <p style={{ fontSize: 13, color: tokens.color.textDim }}>Your feedback is anonymous and helps the DJ learn what worked.</p>
        </PanelShell>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PanelShell title="How's the set going?">
        <p style={{ fontSize: 12, color: tokens.color.muted, marginTop: 0 }}>No account needed — this is anonymous.</p>
        <div style={{ display: "flex", gap: 8, margin: "10px 0" }}>
          {EMOJIS.map((e) => (
            <button key={e} onClick={() => setEmoji(e)} style={{
              fontSize: 24, padding: 8, borderRadius: 8, cursor: "pointer",
              background: emoji === e ? tokens.color.accent : tokens.color.surface2,
              border: `1px solid ${emoji === e ? tokens.color.accentDim : tokens.color.border}`,
            }}>{e}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} style={{
              width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 12,
              background: rating === n ? tokens.color.accent : tokens.color.surface2,
              border: `1px solid ${rating === n ? tokens.color.accentDim : tokens.color.border}`,
            }}>{n}</button>
          ))}
        </div>
        <TextField value={text} onChange={setText} placeholder="Favorite moment or comment (optional)" style={{ width: "100%", boxSizing: "border-box", marginBottom: 8 }} />
        <TextField value={trackRequest} onChange={setTrackRequest} placeholder="Track request (optional)" style={{ width: "100%", boxSizing: "border-box" }} />
        <div style={{ marginTop: 12 }}>
          <Button variant="primary" onClick={() => void submit()} disabled={status === "submitting" || (!emoji && !rating && !text.trim())}>
            {status === "submitting" ? "Sending…" : "Send feedback"}
          </Button>
        </div>
        {status === "error" ? <p style={{ fontSize: 12, color: tokens.color.danger, marginTop: 8 }}>Couldn't send that — check your connection and try again.</p> : null}
      </PanelShell>
    </PageShell>
  );
}

/** Structured promoter feedback form — reached via a link the DJ shares directly, still no account. */
export function LivePromoterFeedbackPage({ client, token }: { client: DJSEMApiClient; token: string }) {
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [bestMoment, setBestMoment] = useState("");
  const [weakestMoment, setWeakestMoment] = useState("");
  const [venueNotes, setVenueNotes] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");

  const submit = async () => {
    setStatus("submitting");
    const res = await client.submitLivePromoterFeedback(token, {
      overallRating: overallRating ?? undefined, bestMoment: bestMoment.trim() || undefined,
      weakestMoment: weakestMoment.trim() || undefined, venueNotes: venueNotes.trim() || undefined,
    });
    setStatus(res.ok ? "done" : "error");
  };

  if (status === "done") {
    return (
      <PageShell>
        <PanelShell title="Thanks for the feedback">
          <p style={{ fontSize: 13, color: tokens.color.textDim }}>This will appear in the DJ's Set Learning Report.</p>
        </PanelShell>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PanelShell title="Promoter feedback">
        <label style={{ display: "block", fontSize: 12, color: tokens.color.muted, marginBottom: 4 }}>Overall set rating (1–5)</label>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setOverallRating(n)} style={{
              width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 12,
              background: overallRating === n ? tokens.color.accent : tokens.color.surface2,
              border: `1px solid ${overallRating === n ? tokens.color.accentDim : tokens.color.border}`,
            }}>{n}</button>
          ))}
        </div>
        <label style={{ display: "block", fontSize: 12, color: tokens.color.muted, marginBottom: 4 }}>Best moment</label>
        <TextField value={bestMoment} onChange={setBestMoment} style={{ width: "100%", boxSizing: "border-box", marginBottom: 8 }} />
        <label style={{ display: "block", fontSize: 12, color: tokens.color.muted, marginBottom: 4 }}>Weakest moment</label>
        <TextField value={weakestMoment} onChange={setWeakestMoment} style={{ width: "100%", boxSizing: "border-box", marginBottom: 8 }} />
        <label style={{ display: "block", fontSize: 12, color: tokens.color.muted, marginBottom: 4 }}>Venue notes</label>
        <TextField value={venueNotes} onChange={setVenueNotes} style={{ width: "100%", boxSizing: "border-box" }} />
        <div style={{ marginTop: 12 }}>
          <Button variant="primary" onClick={() => void submit()} disabled={status === "submitting"}>
            {status === "submitting" ? "Sending…" : "Submit feedback"}
          </Button>
        </div>
        {status === "error" ? <p style={{ fontSize: 12, color: tokens.color.danger, marginTop: 8 }}>Couldn't send that — check your connection and try again.</p> : null}
      </PanelShell>
    </PageShell>
  );
}
